import React from 'react';
import PropTypes from 'prop-types';
import withModalBasics from '../withModalBasics';
import {
  generateTestIds,
  extendTestIds,
  Portal,
  deprecatedProp,
} from '@homebase/common';
import cx from 'classnames';
import Veil from '../Veil';
import CloseButton from '../CloseButton';
import {CSSTransition} from 'react-transition-group';
import './Modal.$BRAND.css.js';

export const sizes = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
};

const ANIMATION_TIMEOUT_MS = 500;

const sizeProps = {
  // The max width of the modal on large displays
  lgSize: PropTypes.oneOf(['small', 'medium', 'large']),
};

const noop = () => {};

export const ModalWrapper = (props) => {
  const modalWrapperStyles = {
    zIndex: props.zIndex,
  };
  return (
    <Portal isOpen={props.isOpen}>
      <div
        className={cx('ResponsiveModal-wrap', {
          'is-scrollable': props.scrollLock,
        })}
        data-enzyme-id="ResponsiveModal"
        data-codeception-id="ResponsiveModal"
        {...generateTestIds({testIds: props.testIds})}
        style={modalWrapperStyles}
      >
        <CSSTransition
          in={!props.willClose}
          appear
          timeout={ANIMATION_TIMEOUT_MS}
          classNames="ResponsiveModal"
        >
          <div
            className={cx({
              ResponsiveModal: true,
              [`locks-at--${props.lgSize}`]: !!props.lgSize,
              'ResponsiveModal--centered': props.center,
              'ResponsiveModal--closeHidden': props.hideClose,
              'ResponsiveModal--disabledOverflow': props.disableOverflow,
            })}
            ref={props.setRef}
            // We want to allow the user to tab to this element since it contains the content of the modal
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex="0"
            role="dialog"
            // http://w3c.github.io/aria/#aria-modal
            aria-modal={props.isDialogModal}
            data-hb-id="Modal"
          >
            {props.children}
          </div>
        </CSSTransition>
        <Veil
          hasElevatedIndex
          handleClick={props.handleOverlayClick}
          isVisible={props.shouldShowOverlay}
          innerRef={props.setLastTabbableRef}
          willClose={props.willClose}
        />
      </div>
    </Portal>
  );
};

ModalWrapper.propTypes = {
  ...sizeProps,
  // If true, the modal will render on the page
  isOpen: PropTypes.bool.isRequired,
  // if true, this will prevent the page from being scrolled while the modal is open
  scrollLock: PropTypes.bool,
  // @ignore-prop-docs
  setRef: PropTypes.func,
  // If true, the modal will center align any of its contents
  center: PropTypes.bool,
  // The rendered content within the modal body
  children: PropTypes.node.isRequired,
  // The function for handling clicks on the veil of the modal
  handleOverlayClick: PropTypes.func,
  // If true, this hides the close button
  hideClose: PropTypes.bool,
  // Controlled from withModalBasics, determines if this modal shows an overlay or not
  shouldShowOverlay: PropTypes.bool,
  // @ignore-prop-docs
  setLastTabbableRef: PropTypes.func,
  // If true, dialog is "modal"  where interaction is limited to dialog contents
  isDialogModal: PropTypes.bool,
  // @ignore-prop-docs - passed down via with modal basics
  setScrollableRef: PropTypes.func,
  // @ignore-prop-docs - pass through prop used by veil component
  willClose: PropTypes.bool,
  // @ignore-prop-docs - If true, overrides overflow-y: auto
  // Temp prop so Custom Select can be used inside Responsive Modal
  disableOverflow: PropTypes.bool,
  // Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // If passed, overrides z-index property defined in CSS
  zIndex: PropTypes.string,
};
ModalWrapper.defaultProps = {
  // @ignore-prop-docs
  setRef: noop,
  scrollLock: false,
  handleOverlayClick: noop,
  center: false,
  hideClose: false,
  shouldShowOverlay: true,
  // @ignore-prop-docs
  setLastTabbableRef: noop,
  isDialogModal: true,
  // @ignore-prop-docs - passed down via with modal basics
  setScrollableRef: noop,
  // @ignore-prop-docs
  disableOverflow: false,
  testIds: {},
  zIndex: null,
};

export const ModalHeader = ({children}) => (
  <div className="ResponsiveModal-header">{children}</div>
);
ModalHeader.propTypes = {
  // The rendered content within the modal header
  children: PropTypes.node.isRequired,
};
export const ModalTitle = ({children}) => (
  <h2 className="ResponsiveModal-title">{children}</h2>
);
ModalTitle.propTypes = {
  // The rendered content within the modal title
  children: PropTypes.node.isRequired,
};
export const ModalCloseButton = (props) =>
  !props.hideClose && (
    <div className="ResponsiveModal-closeWrap">
      <CloseButton
        onClick={props.onClick}
        dataEventName={props.spv2EventName}
        data-enzyme-id="ModalClose"
        data-codeception-id="ResponsiveModal-close"
        testIds={extendTestIds({testIds: props.testIds, postfix: 'close'})}
      />
    </div>
  );
ModalCloseButton.propTypes = {
  // If provided, this will be the spv2 event name
  // @ignore-prop-docs
  spv2EventName: deprecatedProp('Please use a tracking component instead'),
  // If true, this will not render the close modal button
  hideClose: PropTypes.bool,
  // The function that is called when the close button is clicked
  onClick: PropTypes.func,
  // Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
};
ModalCloseButton.defaultProps = {
  onClick: noop,
  // @ignore-prop-docs
  spv2EventName: null,
  hideClose: false,
  testIds: {},
};

export const ModalContent = ({children}) => (
  <div className="ResponsiveModal-content">{children}</div>
);
ModalContent.propTypes = {
  // The rendered content within the modal
  children: PropTypes.node.isRequired,
};

/**
 * FunctionalResponsiveModal
 *
 * Basically a way to funnel props down to a composible child function
 * This allows others to build up a modal with custom wrappers and content
 */
export const FunctionalModal = (props) => {
  if (!props.isOpen) {
    return null;
  }
  // Construct props to hand down to the components
  const modalWrapperProps = {
    lgSize: props.lgSize,
    isOpen: props.isOpen,
    setRef: props.setRef,
    center: props.center,
    scrollLock: props.scrollLock,
    handleOverlayClick: props.handleOverlayClick,
    hideClose: props.hideClose,
    shouldShowOverlay: props.shouldShowOverlay,
    setLastTabbableRef: props.setLastTabbableRef,
    isDialogModal: props.isDialogModal,
    setScrollableRef: props.setScrollableRef,
    willClose: props.willClose,
    disableOverflow: props.disableOverflow,
    testIds: props.testIds,
    zIndex: props.zIndex,
  };
  const modalCloseProps = {
    spv2EventName: props.spv2EventName,
    onClick: props.onRequestClose,
    hideClose: props.hideClose,
    testIds: props.testIds,
  };
  // This allows implementers to construct their own Modal component
  // by giving them back the props and they can decide the arrangement of the pieces/content
  // Very rarely will someone want to use this to build a custom component so by default we export the composed version `Modal`
  return props.children({
    modalWrapperProps,
    modalCloseProps,
    title: props.title,
  });
};
FunctionalModal.propTypes = {
  ...sizeProps,
  // The rendered content within the modal body
  children: PropTypes.func.isRequired,
  // If true, the modal will render on the page
  isOpen: PropTypes.bool,
  // The content rendered as the modal title
  title: PropTypes.node,
  // The function called when the modal is being closed
  onRequestClose: PropTypes.func,
  // If provided, this will be the spv2 event name
  // @ignore-prop-docs
  spv2EventName: deprecatedProp('Please use a tracking component instead'),
  // @ignore-prop-docs
  setRef: PropTypes.func,
  // if true, this will not render a close button
  hideClose: PropTypes.bool,
  // if true, this will prevent the page from being scrolled while the modal is open
  scrollLock: PropTypes.bool,
  // The function called when a user clicks the veil behind the modal
  handleOverlayClick: PropTypes.func,
  // Controlled from withModalBasics, determines if this modal shows an overlay or not
  shouldShowOverlay: PropTypes.bool,
  // @ignore-prop-docs
  setLastTabbableRef: PropTypes.func,
  // If true, dialog is "modal"  where interaction is limited to dialog contents
  isDialogModal: PropTypes.bool,
  // @ignore-prop-docs - passed down via with modal basics
  setScrollableRef: PropTypes.func,
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // If passed, overrides z-index property defined in CSS
  zIndex: PropTypes.string,
};
FunctionalModal.defaultProps = {
  onRequestClose: noop,
  // @ignore-prop-docs
  setRef: noop,
  hideClose: false,
  shouldShowOverlay: true,
  // @ignore-prop-docs
  setLastTabbableRef: noop,
  isDialogModal: true,
  // @ignore-prop-docs
  setScrollableRef: noop,
  testIds: {},
  zIndex: null,
};

const ModalImpl = (props) => (
  <FunctionalModal {...props}>
    {({modalWrapperProps, modalCloseProps, title}) => (
      <ModalWrapper {...modalWrapperProps}>
        <ModalHeader>
          <ModalCloseButton {...modalCloseProps} />
          <ModalTitle>{title}</ModalTitle>
        </ModalHeader>
        <ModalContent>{props.children}</ModalContent>
      </ModalWrapper>
    )}
  </FunctionalModal>
);

ModalImpl.propTypes = {
  // The max width of the modal on large displays
  lgSize: PropTypes.oneOf(['small', 'medium', 'large']),
  // The rendered content within the modal body
  children: PropTypes.node.isRequired,
  // If true, the modal will render on the page
  isOpen: PropTypes.bool,
  // The content rendered as the modal title
  title: PropTypes.node,
  // The function called when the modal is being closed
  onRequestClose: PropTypes.func,
  // If provided, this will be the spv2 event name
  // @ignore-prop-docs
  spv2EventName: deprecatedProp('Please use a tracking component instead'),
  // @ignore-prop-docs
  setRef: PropTypes.func,
  // if true, this will not render a close button
  hideClose: PropTypes.bool,
  // if true, this will prevent the page from being scrolled while the modal is open
  scrollLock: PropTypes.bool,
  // The function called when a user clicks the veil behind the modal
  handleOverlayClick: PropTypes.func,
  // Controlled from withModalBasics, determines if this modal shows an overlay or not
  shouldShowOverlay: PropTypes.bool,
  // @ignore-prop-docs
  setLastTabbableRef: PropTypes.func,
  // If true, dialog is "modal"  where interaction is limited to dialog contents
  isDialogModal: PropTypes.bool,
  // @ignore-prop-docs - passed down via with modal basics
  setScrollableRef: PropTypes.func,
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // If passed, overrides z-index property defined in CSS
  zIndex: PropTypes.string,
};

ModalImpl.defaultProps = {
  onRequestClose: noop,
  // @ignore-prop-docs
  setRef: noop,
  hideClose: false,
  shouldShowOverlay: true,
  // @ignore-prop-docs
  setLastTabbableRef: noop,
  isDialogModal: true,
  // @ignore-prop-docs
  setScrollableRef: noop,
  testIds: {},
  zIndex: null,
};

ModalImpl.displayName = 'Modal';

const Modal = withModalBasics(ModalImpl);

Modal.FunctionalModal = FunctionalModal;
Modal.ModalWrapper = ModalWrapper;
Modal.ModalCloseButton = ModalCloseButton;
Modal.ModalContent = ModalContent;
Modal.ModalTitle = ModalTitle;
Modal.ModalHeader = ModalHeader;
Modal.sizes = sizes;

export default Modal;
